import type { WizardStepDto } from '@bk/contracts';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../ui';

/**
 * I comandi del titolare sul percorso del contratto (ADR 0172): torna alla fase precedente, ferma la fase che sta
 * lavorando (o la riprende), esci dal contratto per usare Timonio liberamente. Li decide la pagina del percorso e li
 * mostra in due posti: il tasto «Comandi» nella testata, sempre, e una fila di pulsanti dove il titolare guarda (il
 * pannello del lavoro in corso, la riga di «Avanti»). Nessuno compare da solo e nessuno cancella un dato.
 */
export interface PhaseCommandsState {
  /** La fase prima di quella aperta (o l'ultima, da «La tua azienda»); null alla prima fase. */
  prev: WizardStepDto | null;
  /** La fase aperta sta lavorando (lavori in corso o contatore aperto). */
  working: boolean;
  /** La fase aperta è ferma per volontà del titolare. */
  stopped: boolean;
  /** Un comando è in corso: gli altri aspettano. */
  pending: boolean;
  back: () => void;
  stop: () => void;
  resume: () => void;
  exit: () => void;
}

const Ctx = createContext<PhaseCommandsState | null>(null);

export function PhaseCommandsProvider({
  value,
  children,
}: {
  value: PhaseCommandsState | null;
  children: ReactNode;
}) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePhaseCommands = () => useContext(Ctx);

/** Il tasto «Comandi» nella testata Sinapsi: apre il pannello con i tre comandi e la riga di cosa succede. */
export function PhaseCommands() {
  const c = usePhaseCommands();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  if (!c) return null;
  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <div className="sn-cmd" ref={box}>
      <button
        type="button"
        className="exit"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Comandi {open ? '▴' : '▾'}
      </button>
      {open && (
        <div className="sn-cmd-menu" role="menu" aria-label="Comandi del percorso">
          <button
            type="button"
            role="menuitem"
            className="sn-cmd-row"
            disabled={!c.prev || c.pending}
            onClick={run(c.back)}
          >
            <b>← Torna alla fase precedente</b>
            <span>
              {c.prev
                ? `Riapre «${c.prev.titolo}»: la rivedi e la giudichi di nuovo; le fasi dopo si aggiornano da ciò che cambia.`
                : 'Sei alla prima fase: non c’è una fase prima.'}
            </span>
          </button>
          {c.stopped ? (
            <button
              type="button"
              role="menuitem"
              className="sn-cmd-row"
              disabled={c.pending}
              onClick={run(c.resume)}
            >
              <b>▶ Riprendi la fase</b>
              <span>Il consulente riprende a lavorare su questa fase con i dati di adesso.</span>
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="sn-cmd-row"
              disabled={c.pending}
              onClick={run(c.stop)}
            >
              <b>■ Ferma la fase</b>
              <span>
                {c.working
                  ? 'Smetto di aspettare il lavoro in corso: la fase si apre con ciò che c’è e puoi andare avanti o indietro.'
                  : 'La fase non sta lavorando adesso: resta ferma finché non la riprendi o cambi fase.'}
              </span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="sn-cmd-row"
            disabled={c.pending}
            onClick={run(c.exit)}
          >
            <b>Esci dal contratto</b>
            <span>
              Si apre il menu di Timonio: il percorso resta dov’è e ci torni con «Torna al
              contratto».
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * La fila dei comandi dove il titolare guarda: nel pannello «Il consulente al lavoro» (chiaro, dentro il passo) e nella
 * pagina scura della fase 0. Stessi comandi del tasto in testata, in forma di pulsanti.
 */
export function PhaseCommandBar({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  const c = usePhaseCommands();
  if (!c) return null;
  const prevLabel = `← Fase precedente${c.prev ? `: ${c.prev.titolo.toLowerCase()}` : ''}`;
  if (tone === 'light')
    return (
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-black/5 pt-4">
        <Button variant="ghost" disabled={!c.prev || c.pending} onClick={c.back}>
          {prevLabel}
        </Button>
        {c.stopped ? (
          <Button variant="ghost" disabled={c.pending} onClick={c.resume}>
            ▶ Riprendi la fase
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="text-red-700 hover:bg-red-50 hover:text-red-800"
            disabled={c.pending}
            onClick={c.stop}
          >
            ■ Ferma la fase
          </Button>
        )}
        <span className="flex-1" />
        <Button variant="ghost" disabled={c.pending} onClick={c.exit}>
          Esci dal contratto
        </Button>
      </div>
    );
  return (
    <div className="sn-cmdbar">
      <button
        type="button"
        className="sn-btn ghost sm"
        disabled={!c.prev || c.pending}
        onClick={c.back}
      >
        {prevLabel}
      </button>
      {c.stopped ? (
        <button type="button" className="sn-btn ghost sm" disabled={c.pending} onClick={c.resume}>
          ▶ Riprendi la fase
        </button>
      ) : (
        <button
          type="button"
          className="sn-btn ghost sm stop"
          disabled={c.pending}
          onClick={c.stop}
        >
          ■ Ferma la fase
        </button>
      )}
      <span style={{ flex: 1 }} />
      <button type="button" className="sn-btn ghost sm" disabled={c.pending} onClick={c.exit}>
        Esci dal contratto
      </button>
    </div>
  );
}

/** Il solo «← Fase precedente», accanto ad «Avanti». */
export function PrevPhaseButton({ tone = 'light' }: { tone?: 'dark' | 'light' }) {
  const c = usePhaseCommands();
  if (!c?.prev) return null;
  const label = `← ${c.prev.titolo}`;
  return tone === 'light' ? (
    <Button variant="ghost" disabled={c.pending} onClick={c.back}>
      {label}
    </Button>
  ) : (
    <button type="button" className="sn-btn ghost sm" disabled={c.pending} onClick={c.back}>
      {label}
    </button>
  );
}
